const TARGET_TYPES_WITH_ID = new Set([
    'board',
    'board-space',
    'milestone-chat',
    'milestone-subtask-summary'
]);

/**
 * Normalize raw websocket subscription targets before they are sent to the
 * backend or stored in the local subscription registry.
 *
 * Supported target shapes:
 * - Resource subscriptions use an id:
 *   { type: 'board', id: 7 }
 *   { type: 'board-space', id: 12 }
 *   { type: 'milestone-chat', id: '42' }
 *   { type: 'milestone-subtask-summary', id: 42 }
 * - Board AI stream subscriptions use a request-scoped routing key:
 *   { type: 'board-ai', stream_routing_key: 'stream-123' }
 *
 * Resource ids are normalized to numbers. Board AI routing keys are trimmed
 * strings. Invalid, incomplete, or unsupported targets return null.
 *
 * @param {Object} target
 * @param {string} target.type - Target group type.
 * @param {number|string} [target.id] - Required for resource subscriptions.
 * @param {string} [target.stream_routing_key] - Required for board AI streams.
 * @returns {{type: string, id?: number, stream_routing_key?: string}|null}
 */
export function normalizeWebsocketTarget(target) {
    if (!target || typeof target !== 'object') {
        return null;
    }

    if (target.type === 'board-ai') {
        const streamRoutingKey = String(target.stream_routing_key || '').trim();

        if (!streamRoutingKey) {
            return null;
        }

        return {
            type: 'board-ai',
            stream_routing_key: streamRoutingKey,
        };
    }

    if (!TARGET_TYPES_WITH_ID.has(target.type)) {
        return null;
    }

    const id = Number.parseInt(target.id, 10);
    if (Number.isNaN(id)) {
        return null;
    }

    return {
        type: target.type,
        id,
    };
}

export function normalizeWebsocketTargets(targets = []) {
    const targetList = Array.isArray(targets) ? targets : [targets];

    return targetList
        .map(normalizeWebsocketTarget)
        .filter(Boolean);
}

export function serializeWebsocketTarget(target) {
    if (target.type === 'board-ai') {
        return `${target.type}:${target.stream_routing_key}`;
    }

    return `${target.type}:${target.id}`;
}

export function addWebsocketTargets(entries = {}, targets = []) {
    const nextEntries = { ...entries };
    const addedTargets = [];

    for (const target of normalizeWebsocketTargets(targets)) {
        const key = serializeWebsocketTarget(target);
        const existingEntry = nextEntries[key];

        if (existingEntry) {
            nextEntries[key] = {
                ...existingEntry,
                // Purpose of count: multiple components may need the same target. Keep one
                // backend subscription active until the final subscriber exits.
                count: existingEntry.count + 1,
            };
            continue;
        }

        nextEntries[key] = { target, count: 1 };
        addedTargets.push(target);
    }

    return {
        entries: nextEntries,
        addedTargets,
    };
}

export function removeWebsocketTargets(entries = {}, targets = []) {
    const nextEntries = { ...entries };
    const removedTargets = [];

    for (const target of normalizeWebsocketTargets(targets)) {
        const key = serializeWebsocketTarget(target);
        const existingEntry = nextEntries[key];

        if (!existingEntry) {
            continue;
        }

        if (existingEntry.count > 1) {
            nextEntries[key] = {
                ...existingEntry,
                // Keep the backend subscription while another component still
                // depends on this target.
                count: existingEntry.count - 1,
            };
            continue;
        }

        delete nextEntries[key];
        removedTargets.push(existingEntry.target);
    }

    return {
        entries: nextEntries,
        removedTargets,
    };
}

export function getSubscribedWebsocketTargets(entries = {}) {
    return Object.values(entries).map(({ target }) => target);
}
